import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getRequest } from '../../utils/axiosUtils';
import { downloadCsv } from '../../utils/fileUtils';

/**
 * Daily Drop-off by Step — the "Graph View" of the Onboarding Drop-off page.
 *
 * A faithful React port of the favor-daily-dropoff design: every step transition of
 * sign-up, every day, on one page — either as a per-row calendar heat-strip or as
 * per-transition charts (bars / lollipop / deviation / line / weekday).
 *
 * Unlike the reference HTML (which used a generated cohort), this runs on LIVE data:
 * the parent (GhostFunnel) passes `daily` = per-IST-day furthest-step distribution
 * (from GET /admin/attribution/ghost-funnel), already filtered to the selected period
 * and platform server-side. Gender comes from the page's Gender pills via `genderKey`.
 *
 * Each day's dist[] is cumulated into reached[] (reached[s] = Σ dist[j>=s]); the
 * per-step daily "users lost" = reached[k-1] − reached[k], matching the overall funnel.
 *
 * DRILL-DOWN: every cell is clickable — it opens "who dropped here & why" for that
 * (transition, day), fetched from GET /admin/attribution/ghost-funnel-users. Because the
 * cell counts the users whose FURTHEST step is exactly k, the endpoint asks for exactly
 * that, over the same cohort — so the list length equals the number that was clicked.
 * For More photos → Bio the panel also answers WHY, from the member's own bio
 * submissions and how moderation answered them. Email → OTP used to carry the same treatment
 * off the SES event store; that row left the taxonomy on 2026-09-01 when signup went
 * Google/Apple-only, and the panel went with it.
 *
 * Scoped under `.favor-dd` so its styles never touch the admin's global CSS; typography
 * uses the admin app's own families (ivypresto-headline serif + indivisible sans).
 */

// More photos → Bio: what the bio filter did to the people who dropped here — chip
// label, colour class, and what to actually do about it.
const ABOUT_CATS = [
  {
    k: 'blocked',
    label: 'Wrote a bio — refused every time',
    cls: 'b',
    act: 'This is the drop the filter caused. Read the texts below before tuning anything: if they look like ordinary bios, the prompt is too strict and these members were turned away for nothing.',
  },
  {
    k: 'passed',
    label: 'Bio accepted — left anyway',
    cls: 'o',
    act: 'Moderation is not why these left: their text was saved and they still stopped. That is a product question about the screen that follows.',
  },
  {
    k: 'never',
    label: 'Never typed anything',
    cls: 'n',
    act: 'They left on the photo screen without ever opening the bio one. Nothing to read, and nothing moderation could have done.',
  },
];

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const r1 = (x) => Math.round(x * 10) / 10;
const r0 = Math.round;

// Palettes — every bucket's number clears 4.5:1 against its own fill (lifted verbatim
// from the reference so both the calendar and the graphs read the same colour scale).
const PALETTES = {
  coral: { name: 'Coral', ring: '#8f2318', ramp: [['#ffffff', '#25313f'], ['#fbe7e4', '#25313f'], ['#f5c8c2', '#25313f'], ['#eea096', '#25313f'], ['#c74839', '#ffffff'], ['#8f2318', '#ffffff']] },
  slate: { name: 'Slate', ring: '#1d2733', ramp: [['#ffffff', '#25313f'], ['#e6e8eb', '#25313f'], ['#c5cad1', '#25313f'], ['#9ba4af', '#25313f'], ['#4a5a6e', '#ffffff'], ['#1d2733', '#ffffff']] },
  oxblood: { name: 'Oxblood', ring: '#4a1119', ramp: [['#ffffff', '#25313f'], ['#ece0e1', '#25313f'], ['#d4b7bb', '#25313f'], ['#ba8b91', '#25313f'], ['#7a1f2b', '#ffffff'], ['#4a1119', '#ffffff']] },
  amber: { name: 'Amber', ring: '#6a4708', ramp: [['#ffffff', '#25313f'], ['#f6efe2', '#25313f'], ['#ebdabc', '#25313f'], ['#dcbf8c', '#25313f'], ['#956c24', '#ffffff'], ['#6a4708', '#ffffff']] },
  indigo: { name: 'Indigo', ring: '#1e3a78', ramp: [['#ffffff', '#25313f'], ['#e4ebfa', '#25313f'], ['#c2d1f3', '#25313f'], ['#95b0ea', '#25313f'], ['#3f6fd8', '#ffffff'], ['#1e3a78', '#ffffff']] },
  plum: { name: 'Plum', ring: '#3a2359', ramp: [['#ffffff', '#25313f'], ['#ebe6f2', '#25313f'], ['#d0c5e1', '#25313f'], ['#af9bcb', '#25313f'], ['#6d4aa0', '#ffffff'], ['#3a2359', '#ffffff']] },
  forest: { name: 'Forest', ring: '#14472c', ramp: [['#ffffff', '#25313f'], ['#e2efe8', '#25313f'], ['#bcdbcb', '#25313f'], ['#8dc1a5', '#25313f'], ['#2c8656', '#ffffff'], ['#14472c', '#ffffff']] },
};


// Every calendar day in [from, to] inclusive — so the grid keeps its full window width
// (e.g. 30 columns for "Last 30 days") and days without data render blank, matching the
// original design instead of collapsing to only the days that happen to have sign-ups.
const addDay = (iso) => { const dt = new Date(iso + 'T00:00:00Z'); dt.setUTCDate(dt.getUTCDate() + 1); return dt.toISOString().slice(0, 10); };
const eachDay = (from, to) => { const out = []; let d = from; let guard = 0; while (from && to && d <= to && guard < 400) { out.push(d); d = addDay(d); guard += 1; } return out; };

const DOWFULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const tfmt = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '—';
  let h = t.getHours(); const m = String(t.getMinutes()).padStart(2, '0');
  const ap = h < 12 ? 'am' : 'pm'; h = h % 12 || 12;
  return `${h}:${m} ${ap}`;
};
// Full date+time for the CSV — the on-screen table shows only the time because the day
// is the panel's title, but an exported file has to stand on its own.
const csvDT = (iso) => {
  if (!iso) return '';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${t.getFullYear()}-${p(t.getMonth() + 1)}-${p(t.getDate())} ${p(t.getHours())}:${p(t.getMinutes())}`;
};
// One member's About story in a word: nothing typed / saved / turned away. Same three
// buckets the server counts for the summary chips, so the column, the chips and the CSV
// can never disagree.
const aboutVerdict = (list) => {
  if (!list || !list.length) return 'never';
  return list.some((a) => a.accepted) ? 'passed' : 'blocked';
};

const DailyDropoff = ({ daily, steps, genderKey, platform, windowFrom, windowTo, canonicalGhost, cohort }) => {
  const ghostCohort = cohort === 'ghost';
  // Indigo by default — the palette the reporting views read best in.
  const [pal, setPal] = useState('indigo');
  const [view, setView] = useState('cal'); // cal | graph
  const [chart, setChart] = useState('bars'); // bars | lolli | dev | line | week
  const [metric, setMetric] = useState('lost'); // lost | rate | entry

  // Drill-down: the clicked cell {k, d}, and the users the server returns for it. `k` is
  // the transition index (STEPS[k-1] → STEPS[k]); the users lost there are exactly those
  // whose furthest canonical step is k, which is what the endpoint takes.
  const [sel, setSel] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState(false);
  const drillRef = useRef(null);

  // The calendar owns its horizontal scroll (the day columns can run far wider than
  // the card), and a box that scrolls on one axis scrolls on both — so a sticky
  // header inside it can only pin to the box, never to the page. When the grid is
  // narrow enough to need no horizontal scroll we drop the overflow entirely
  // (`.fits`), which puts the whole table back on the page scroll and lets the day
  // header stick under the app's navbar. Wide windows keep the self-scrolling box.
  const calRef = useRef(null);
  const [gridFits, setGridFits] = useState(false);

  const rows = daily || [];
  const STEPS = useMemo(() => (steps || []).map((s) => s.label), [steps]);
  const N = STEPS.length;
  const byDate = useMemo(() => {
    const m = {};
    rows.forEach((d) => { m[d.date] = d; });
    return m;
  }, [rows]);
  // The days to render are exactly the ones the parent returned — the page-level Period
  // filter (incl. Custom from/to) is the single source of the date window, so this view
  // has no date picker of its own.
  // Render the full selected window (blank where no data); fall back to just the data
  // days if the parent didn't pass a window.
  const ALL_DATES = useMemo(
    () => (windowFrom && windowTo ? eachDay(windowFrom, windowTo) : rows.map((d) => d.date).slice().sort()),
    [windowFrom, windowTo, rows],
  );

  const P = PALETTES[pal];
  const zero = useMemo(() => new Array(N).fill(0), [N]);
  // The commitment point of the funnel — the step where a sign-up stops answering
  // questions and starts building a profile other members will see.
  //
  // This USED to be the "Complete Profile" / "I'll do it later" gate. The 2026-08-25
  // reorder removed that screen from both clients, so it is no longer in the taxonomy and
  // the old regex found nothing — falling back to a hardcoded index 10, which after the
  // renumbering points at Relationship status and made this KPI quietly meaningless.
  // Anchored on the photo step instead, found by KEY off the server's catalogue.
  const GATE = useMemo(() => {
    const i = (steps || []).findIndex((s) => s?.key === 'profile_picture');
    return i >= 0 ? i : Math.max(0, N - 5);
  }, [steps, N]);

  const fmt = (v) => (metric === 'lost' ? String(v) : r1(v));
  const unit = () => (metric === 'lost' ? '' : '%');
  // Compact label so ~30 of them fit side by side on the graphs.
  const glab = (v) => (metric === 'lost' ? String(v) : v >= 10 ? String(r0(v)) : String(r1(v)));

  // ── build the per-transition daily table from the cumulated daily distributions ──
  const model = useMemo(() => {
    const ds = ALL_DATES;
    const reach = Array.from({ length: N }, () => new Array(ds.length).fill(0));
    const entry = new Array(ds.length).fill(0);
    const blank = ds.map((d) => !byDate[d]); // days in the window with no sign-up data
    ds.forEach((d, i) => {
      const day = byDate[d];
      const distArr = day ? (day.dist[genderKey] || day.dist.all) : zero;
      let cum = 0;
      for (let s = N - 1; s >= 0; s--) { cum += distArr[s] || 0; reach[s][i] = cum; }
      entry[i] = reach[0][i];
    });
    const out = [];
    for (let k = 1; k < N; k++) {
      out.push({
        k,
        rows: ds.map((d, i) => {
          const before = reach[k - 1][i];
          const lost = before - reach[k][i];
          return { d, blank: blank[i], lost, before, rate: before ? (lost / before) * 100 : 0, entry: entry[i] ? (lost / entry[i]) * 100 : 0 };
        }),
      });
    }
    return { ds, out, reach };
  }, [ALL_DATES, byDate, genderKey, N, zero]);

  const stats = (rw) => {
    const s = rw.filter((r) => !r.blank).map((r) => r[metric]); // mean/σ over data days only
    const mu = s.reduce((a, b) => a + b, 0) / (s.length || 1);
    return { mu, sd: Math.sqrt(s.reduce((a, b) => a + (b - mu) ** 2, 0) / (s.length || 1)) };
  };
  const z = (v, st) => (st.sd > 1e-9 ? (v - st.mu) / st.sd : 0);
  const bucket = (v, st) => { const t = z(v, st); return t <= -1 ? 0 : t <= -0.25 ? 1 : t <= 0.4 ? 2 : t <= 1.2 ? 3 : t <= 2 ? 4 : 5; };

  const { ds, out, reach } = model;
  const per = useMemo(() => out.map((o) => ({ k: o.k, rows: o.rows, st: stats(o.rows), total: o.rows.reduce((a, r) => a + r.lost, 0) })), [out, metric]);

  const rowTotal = (r) => (r ? r.reduce((a, b) => a + b, 0) : 0);
  const signups = rowTotal(reach[0]);
  // N - 1 is the funnel's TERMINAL step ("Profile complete"), which is not a screen — so
  // the last screen anybody actually navigates to is N - 2. Reading the terminal here is
  // what would make this KPI quietly report the completion count instead. The label comes
  // from the catalogue too: the last screen has already been renamed once.
  const reachedLastScreen = rowTotal(reach[N - 2]);
  const lastScreenLabel = STEPS[N - 2] || 'the last screen';
  const completedProfile = rowTotal(reach[N - 1]);
  const beforeGate = signups - rowTotal(reach[GATE]);
  // On the ghost cohort the entry count IS the ghost count — every member drawn here is
  // one of the Ghost Users the card counted, which is the whole point of the default view.
  // So `canonicalGhost` stops being the number we display and becomes an ASSERTION: if the
  // page ever drifts from the card again, say so on screen instead of quietly showing two
  // different totals and leaving whoever is reading to spot it. Disagreeing numbers are a
  // finding, not something to hide.
  const ghostMismatch = ghostCohort && canonicalGhost != null && canonicalGhost !== signups
    ? canonicalGhost
    : null;
  const worst = per.slice().sort((a, b) => b.total - a.total)[0];
  const totalAnom = per.reduce((a, p) => a + p.rows.filter((r) => !r.blank && Math.abs(z(r[metric], p.st)) >= 2).length, 0);

  const rowMeta = (p) => {
    const an = p.rows.filter((r) => !r.blank && Math.abs(z(r[metric], p.st)) >= 2);
    const hot = p.total === Math.max(...per.map((x) => x.total));
    return { an, hot };
  };

  // A cell selected under one set of filters may not exist under the next (a narrower
  // period, a gender the user didn't declare) — so any filter change closes the panel
  // rather than leaving a list on screen that no longer belongs to any visible cell.
  useEffect(() => { setSel(null); setDrill(null); setDrillError(false); }, [genderKey, platform, windowFrom, windowTo, cohort]);

  // Load the people behind the selected cell. Its own URL path (not a flag on
  // /ghost-funnel), so getRequest's abort-by-path can't cancel the matrix fetch that
  // opened it; clicking a second cell DOES cancel the first, which is what we want.
  useEffect(() => {
    if (!sel) return undefined;
    let active = true;
    setDrillLoading(true); setDrillError(false);
    (async () => {
      try {
        // The cell was counted over ONE population; the list has to be drawn over the
        // same one, or it comes back a different size than the number that was clicked.
        const params = { step: sel.k, day: sel.d, cohort: ghostCohort ? 'ghost' : 'all' };
        if (platform && platform !== 'all') params.platform = platform;
        if (genderKey && genderKey !== 'all') params.gender = genderKey;
        const res = await getRequest({ url: '/admin/attribution/ghost-funnel-users', params });
        if (!active) return;
        if (!res) return; // superseded by a newer cell click — not a failure
        const body = res?.data;
        const payload = Array.isArray(body?.users) ? body : body?.data;
        if (payload && Array.isArray(payload.users)) setDrill(payload);
        else setDrillError(true);
      } catch (e) { if (active) setDrillError(true); }
      finally { if (active) setDrillLoading(false); }
    })();
    return () => { active = false; };
  }, [sel, platform, genderKey, ghostCohort]);

  // Bring the panel into view on open — it renders below the (tall) matrix.
  useEffect(() => {
    if (sel && drillRef.current) drillRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [sel]);

  const onCellClick = (e) => {
    const td = e.target.closest('td.c');
    if (!td || td.classList.contains('blank') || !td.dataset.k) return;
    setSel({ k: Number(td.dataset.k), d: td.dataset.d });
  };

  // ── linear calendar (row per transition, cell per day) ──
  const calHtml = useMemo(() => {
    if (!ds.length) return '';
    let head = `<thead><tr><th class="rl"><span class="lab">Transition</span></th>`;
    ds.forEach((d, i) => {
      const dt = new Date(d + 'T00:00:00'); const dw = dt.getDay();
      const newMonth = i === 0 || new Date(ds[i - 1] + 'T00:00:00').getMonth() !== dt.getMonth();
      head += `<th class="dh ${newMonth ? 'ms' : ''} ${dw === 0 || dw === 6 ? 'we' : ''}" title="${d}">
        <span class="mo">${newMonth ? MON[dt.getMonth()] : ''}</span>
        <span class="dw">${DOW[dw]}</span><span class="dn">${dt.getDate()}</span></th>`;
    });
    head += `</tr></thead>`;
    const body = per.map((p) => {
      const { an, hot } = rowMeta(p);
      let tr = `<tr class="${hot ? 'hot' : ''}"><th class="rl">
        <div><span class="rn">${String(p.k).padStart(2, '0')}</span><span class="rt">${STEPS[p.k]}</span></div>
        <div class="rm">mean <b>${metric === 'lost' ? r0(p.st.mu) : r1(p.st.mu) + unit()}</b>/day &middot; &#963; ${r1(p.st.sd)}${unit()}
          ${an.length ? `&middot; <span class="flag">${an.length} anomalous</span>` : ''}${hot ? ' &middot; <span class="flag">biggest leak</span>' : ''}</div></th>`;
      p.rows.forEach((r, i) => {
        const dt = new Date(r.d + 'T00:00:00');
        const newMonth = i === 0 || new Date(ds[i - 1] + 'T00:00:00').getMonth() !== dt.getMonth();
        if (r.blank) { // day in the window with no data — blank cell, keeps the grid intact
          tr += `<td class="c blank ${newMonth ? 'ms' : ''}" title="${dt.getDate()} ${MON[dt.getMonth()]} — no data"></td>`;
          return;
        }
        const v = r[metric]; const bk = bucket(v, p.st); const b = P.ramp[bk]; const a = Math.abs(z(v, p.st)) >= 2;
        const ringc = bk >= 4 ? '#ffffff' : P.ring;
        const isSel = sel && sel.k === p.k && sel.d === r.d;
        const tip = `${dt.getDate()} ${MON[dt.getMonth()]} — ${r.lost} lost of ${r.before} who reached ${STEPS[p.k - 1]}`
          + ` · ${r1(r.rate)}% of this step · ${r1(r.entry)}% of that day's signups`
          + (a ? ' · anomalous, ' + r1(z(v, p.st)) + ' sigma' : '')
          + ' · click for the people behind this';
        tr += `<td class="c ${newMonth ? 'ms' : ''} ${a ? 'anom' : ''} ${isSel ? 'sel' : ''}"
          data-k="${p.k}" data-d="${r.d}"
          style="background:${b[0]};color:${b[1]};--ringc:${ringc}" title="${tip}">
          ${a ? '<span class="dot"></span>' : ''}${fmt(v)}</td>`;
      });
      return tr + '</tr>';
    }).join('');
    return head + '<tbody>' + body + '</tbody>';
  }, [ds, per, metric, P, STEPS, sel]);

  // ── graph view (one chart per transition) ──
  const frame = (n) => { const W = Math.max(660, 22 * n + 90); const H = 210; const L = 42; const R = 18; const T = 30; const B = 34; return { W, H, L, R, T, B, iw: W - L - R, ih: H - T - B }; };
  // Column layout that stays legible for 3 days OR 30 — caps the per-day column and bar
  // width and centres the group, so a handful of days don't stretch into giant, smudgy
  // bars spread edge-to-edge (the SVG scales to the container, which magnifies wide bars).
  const cols = (f, n) => {
    const colW = Math.min(f.iw / Math.max(n, 1), 150);
    const x0 = f.L + (f.iw - colW * n) / 2;
    return { X: (i) => x0 + colW * (i + 0.5), bw: Math.min(colW * 0.55, 44) };
  };
  const yaxis = (f, max, mu, showBand) => {
    let g = '';
    [0, max / 2, max].forEach((v) => {
      const y = f.T + f.ih - (v / max) * f.ih;
      g += `<line x1="${f.L}" y1="${y.toFixed(1)}" x2="${f.W - f.R}" y2="${y.toFixed(1)}" stroke="#e3e3e3" stroke-width="1"/>`
        + `<text class="axis" x="${f.L - 7}" y="${(y + 3).toFixed(1)}" text-anchor="end">${metric === 'lost' ? r0(v) : r1(v)}</text>`;
    });
    if (showBand) {
      const Y = (v) => f.T + f.ih - (v / max) * f.ih;
      const hi = Y(Math.min(mu + (showBand.sd || 0), max)); const lo = Y(Math.max(mu - (showBand.sd || 0), 0));
      g = `<rect x="${f.L}" y="${hi.toFixed(1)}" width="${f.iw}" height="${Math.max(lo - hi, 0).toFixed(1)}" fill="${P.ramp[1][0]}"/>` + g;
      g += `<line x1="${f.L}" y1="${Y(mu).toFixed(1)}" x2="${f.W - f.R}" y2="${Y(mu).toFixed(1)}" stroke="#6e7782" stroke-width="1" stroke-dasharray="4 3"/>`
        + `<text class="axis" x="${f.W - f.R}" y="${(Y(mu) - 4).toFixed(1)}" text-anchor="end">mean</text>`;
    }
    return g;
  };
  const xdates = (f, rw, X) => {
    let g = '';
    rw.forEach((r, i) => {
      // Label every day when there are few; thin to every 3rd once it gets crowded.
      if (rw.length > 8 && i % 3 !== 0 && i !== rw.length - 1) return;
      const dt = new Date(r.d + 'T00:00:00');
      g += `<text class="axis" x="${X(i).toFixed(1)}" y="${f.H - 12}" text-anchor="middle">${dt.getDate()} ${MON[dt.getMonth()]}</text>`;
    });
    return g;
  };
  const svgWrap = (f, p, n, g, note) => `<div class="gwrap"><svg viewBox="0 0 ${f.W} ${f.H}" role="img"
    aria-label="never reached ${STEPS[p.k]}, ${n} days">${g}</svg></div><div class="gnote">${note}</div>`;

  const chartBars = (p) => {
    const rw = p.rows; const n = rw.length; const f = frame(n);
    const vals = rw.map((r) => r[metric]);
    const max = Math.max.apply(null, vals.concat([p.st.mu + 2 * p.st.sd, metric === 'lost' ? 1 : 0.5])) * 1.22;
    const { X, bw } = cols(f, n); const Y = (v) => f.T + f.ih - (v / max) * f.ih;
    let g = yaxis(f, max, p.st.mu, { sd: p.st.sd });
    rw.forEach((r, i) => {
      if (r.blank) return;
      const v = r[metric]; const bk = bucket(v, p.st); const a = Math.abs(z(v, p.st)) >= 2;
      const x = X(i); const y = Y(v); const h = Math.max(f.T + f.ih - y, 0);
      g += `<rect x="${(x - bw / 2).toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}"
        rx="2" fill="${P.ramp[Math.max(bk, 1)][0]}" ${a ? `stroke="${P.ring}" stroke-width="1.5"` : ''}/>`
        + `<text class="vlab ${a ? 'hi' : ''}" x="${x.toFixed(1)}" y="${(y - 6).toFixed(1)}" text-anchor="middle">${glab(v)}</text>`;
    });
    g += xdates(f, rw, X);
    return svgWrap(f, p, n, g, 'bars = one day each &middot; shaded band is &#177;1&#963; &middot; ringed bars are beyond 2&#963;');
  };
  const chartLolli = (p) => {
    const rw = p.rows; const n = rw.length; const f = frame(n);
    const vals = rw.map((r) => r[metric]);
    const max = Math.max.apply(null, vals.concat([p.st.mu + 2 * p.st.sd, metric === 'lost' ? 1 : 0.5])) * 1.22;
    const { X } = cols(f, n); const Y = (v) => f.T + f.ih - (v / max) * f.ih;
    let g = yaxis(f, max, p.st.mu, { sd: p.st.sd });
    rw.forEach((r, i) => {
      if (r.blank) return;
      const v = r[metric]; const a = Math.abs(z(v, p.st)) >= 2; const x = X(i); const y = Y(v);
      g += `<line x1="${x.toFixed(1)}" y1="${(f.T + f.ih).toFixed(1)}" x2="${x.toFixed(1)}" y2="${y.toFixed(1)}"
          stroke="${a ? P.ring : P.ramp[3][0]}" stroke-width="1.6"/>`
        + `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${a ? 4.2 : 3}" fill="${a ? P.ring : P.ramp[4][0]}"
          ${a ? 'stroke="#fff" stroke-width="1.4"' : ''}/>`
        + `<text class="vlab ${a ? 'hi' : ''}" x="${x.toFixed(1)}" y="${(y - 8).toFixed(1)}" text-anchor="middle">${glab(v)}</text>`;
    });
    g += xdates(f, rw, X);
    return svgWrap(f, p, n, g, 'one stem per day &middot; shaded band is &#177;1&#963;');
  };
  const chartDev = (p) => {
    const rw = p.rows; const n = rw.length; const f = frame(n);
    const vals = rw.map((r) => r[metric]);
    const dmax = Math.max.apply(null, vals.map((v) => Math.abs(v - p.st.mu)).concat([p.st.sd * 2.4, 0.5])) * 1.18;
    const mid = f.T + f.ih / 2; const Y = (d) => mid - (d / dmax) * (f.ih / 2);
    const { X, bw } = cols(f, n);
    let g = '';
    [1, 2].forEach((k) => { [1, -1].forEach((s) => {
      const y = Y(s * k * p.st.sd);
      g += `<line x1="${f.L}" y1="${y.toFixed(1)}" x2="${f.W - f.R}" y2="${y.toFixed(1)}" stroke="${k === 2 ? '#d9d9d9' : '#ececec'}"
        stroke-width="1" ${k === 2 ? 'stroke-dasharray="3 3"' : ''}/>`
        + `<text class="axis" x="${f.L - 7}" y="${(y + 3).toFixed(1)}" text-anchor="end">${s > 0 ? '+' : '&#8722;'}${k}&#963;</text>`;
    }); });
    g += `<line x1="${f.L}" y1="${mid.toFixed(1)}" x2="${f.W - f.R}" y2="${mid.toFixed(1)}" stroke="#6e7782" stroke-width="1.2"/>`
      + `<text class="axis" x="${f.L - 7}" y="${(mid + 3).toFixed(1)}" text-anchor="end">mean</text>`;
    rw.forEach((r, i) => {
      if (r.blank) return;
      const v = r[metric]; const d = v - p.st.mu; const a = Math.abs(z(v, p.st)) >= 2;
      const x = X(i); const y = Y(d); const top = Math.min(y, mid); const h = Math.abs(y - mid);
      g += `<rect x="${(x - bw / 2).toFixed(1)}" y="${top.toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(h, 1).toFixed(1)}"
          rx="2" fill="${d >= 0 ? P.ramp[a ? 5 : 4][0] : P.ramp[2][0]}"/>`
        + `<text class="vlab ${a ? 'hi' : ''}" x="${x.toFixed(1)}" y="${(d >= 0 ? y - 6 : y + 13).toFixed(1)}" text-anchor="middle">${glab(v)}</text>`;
    });
    g += xdates(f, rw, X);
    return svgWrap(f, p, n, g, 'distance from this step&#8217;s own mean &middot; dashed lines are &#177;2&#963;');
  };
  const chartLine = (p) => {
    const rw = p.rows; const n = rw.length; const f = frame(n);
    const vals = rw.map((r) => r[metric]);
    const max = Math.max.apply(null, vals.concat([p.st.mu + 2 * p.st.sd, metric === 'lost' ? 1 : 0.5])) * 1.22;
    const { X } = cols(f, n); const Y = (v) => f.T + f.ih - (v / max) * f.ih;
    const dv = rw.filter((r) => !r.blank).map((r) => r[metric]);
    const vmax = dv.length ? Math.max(...dv) : 0; const vmin = dv.length ? Math.min(...dv) : 0;
    let g = yaxis(f, max, p.st.mu, { sd: p.st.sd });
    // Connect only the days that have data (skip blank gaps).
    const pts = rw.map((r, i) => ({ i, v: r[metric], blank: r.blank })).filter((o) => !o.blank);
    g += `<path d="${pts.map((o, j) => `${j ? 'L' : 'M'}${X(o.i).toFixed(1)},${Y(o.v).toFixed(1)}`).join('')}"
        fill="none" stroke="${P.ramp[4][0]}" stroke-width="2" stroke-linejoin="round"/>`;
    rw.forEach((r, i) => {
      if (r.blank) return;
      const v = r[metric]; const a = Math.abs(z(v, p.st)) >= 2; const peak = v === vmax || v === vmin; const x = X(i); const y = Y(v);
      g += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${a ? 4.2 : 2.4}" fill="${a ? P.ring : P.ramp[4][0]}"
          ${a ? 'stroke="#fff" stroke-width="1.5"' : ''}/>`
        + `<text class="vlab ${a ? 'hi' : ''}" x="${x.toFixed(1)}" y="${Math.max(y - 9, f.T - 6).toFixed(1)}" text-anchor="middle"
          ${peak && !a ? 'font-weight="700"' : ''}>${glab(v)}</text>`;
    });
    g += xdates(f, rw, X);
    return svgWrap(f, p, n, g, 'a line implies values between days that do not exist &#8212; use bars unless you want the trend');
  };
  const chartWeek = (p) => {
    const order = [1, 2, 3, 4, 5, 6, 0]; const NAME = { 0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat' };
    const g0 = Object.fromEntries(order.map((d) => [d, []]));
    p.rows.forEach((r) => { if (r.blank) return; g0[new Date(r.d + 'T00:00:00').getDay()].push(r[metric]); });
    const n = 7; const f = frame(12);
    const means = order.map((d) => { const a = g0[d]; return a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0; });
    const all = p.rows.map((r) => r[metric]);
    const max = Math.max.apply(null, all.concat([1])) * 1.2;
    const X = (i) => f.L + ((i + 0.5) / n) * f.iw; const Y = (v) => f.T + f.ih - (v / max) * f.ih;
    const bw = (f.iw / n) * 0.5;
    let g = yaxis(f, max, p.st.mu, { sd: 0 });
    order.forEach((d, i) => {
      const m = means[i]; const x = X(i); const y = Y(m);
      const off = Math.abs(m - p.st.mu) > p.st.sd;
      g += `<rect x="${(x - bw / 2).toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(f.T + f.ih - y, 1).toFixed(1)}"
          rx="2" fill="${off ? P.ramp[4][0] : P.ramp[2][0]}"/>`;
      g0[d].forEach((v, j) => { const jx = x - bw / 2 + (j + 0.5) * (bw / Math.max(g0[d].length, 1));
        g += `<circle cx="${jx.toFixed(1)}" cy="${Y(v).toFixed(1)}" r="1.8" fill="#25313f" opacity=".38"/>`; });
      g += `<text class="vlab ${off ? 'hi' : ''}" x="${x.toFixed(1)}" y="${(y - 6).toFixed(1)}" text-anchor="middle">${r1(m)}</text>`
        + `<text class="axis" x="${x.toFixed(1)}" y="${f.H - 12}" text-anchor="middle">${NAME[d]}</text>`;
    });
    return svgWrap(f, p, 7, g, 'average by weekday, with every individual day as a dot &middot; bar is dark when the weekday sits more than 1&#963; off the overall mean');
  };
  const CHARTS = { bars: chartBars, lolli: chartLolli, dev: chartDev, line: chartLine, week: chartWeek };

  const graphHtml = useMemo(() => {
    if (!ds.length) return '';
    return per.map((p) => {
      const { an, hot } = rowMeta(p);
      return `<div class="sc">
        <div class="sc-h"><span class="sc-n">${String(p.k).padStart(2, '0')}</span>
          <span class="sc-t">${STEPS[p.k]}</span></div>
        <div class="sc-m">mean <b>${metric === 'lost' ? r0(p.st.mu) : r1(p.st.mu) + unit()}</b> per day &middot; &#963; ${r1(p.st.sd)}${unit()}
          ${an.length ? `&middot; <span class="flag">${an.length} anomalous</span>` : ''}${hot ? ' &middot; <span class="flag">biggest leak</span>' : ''}</div>
        ${CHARTS[chart](p)}</div>`;
    }).join('');
  }, [ds, per, metric, chart, P, STEPS]);

  const Pills = ({ items, value, onChange, variant }) => (
    <div className="dd-pills">
      {items.map(([k, label]) => (
        <button key={k} type="button" className={`dd-pill${value === k ? ` on ${variant || ''}` : ''}`} onClick={() => onChange(k)}>{label}</button>
      ))}
    </div>
  );

  // Does the grid need a horizontal scrollbar at this width? Re-measured whenever the
  // grid is rebuilt (period / gender / view change) and on resize. Must sit above the
  // empty-state early return so the hook order never changes.
  useLayoutEffect(() => {
    const el = calRef.current;
    if (!el) { setGridFits(false); return undefined; }
    const measure = () => setGridFits(el.scrollWidth <= el.clientWidth + 1);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [calHtml, view]);

  if (!rows.length) {
    return (
      <div className="favor-dd">
        <style>{STYLES}</style>
        <div className="dd-empty">No per-day data in this window. Pick a wider period, or reload the backend if the daily feed hasn’t shipped yet.</div>
      </div>
    );
  }

  return (
    // --dd-accent follows the selected palette so every accent (active pill, anomaly
    // rings/flags, month labels, highlighted values, the 2σ legend swatch) themes
    // together — the cohesive behaviour of the original design.
    <div className="favor-dd" style={{ '--dd-accent': P.ring }}>
      <style>{STYLES}</style>

      {/* Palette — the date window is controlled by the page-level Period filter above */}
      <div className="dd-filters">
        <div className="dd-fg" style={{ alignItems: 'flex-start' }}>
          <span className="dd-lab" style={{ marginTop: 12 }}>Palette</span>
          <div className="dd-pals">
            {Object.keys(PALETTES).map((k) => {
              const p = PALETTES[k];
              return (
                <button key={k} type="button" className="dd-pal" aria-pressed={k === pal} onClick={() => setPal(k)}>
                  <span className="pn">{p.name}</span>
                  <span className="pr">{p.ramp.map((rr, i) => <i key={i} style={{ background: rr[0] }} />)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="dd-kpis">
        <div className="dd-kpi"><div className="k">{ghostCohort ? 'Ghost users' : 'Sign ups'}</div><div className="v serif">{signups}</div>
          <div className="d">
            {ghostMismatch != null
              ? `⚠ Channel Attribution says ${ghostMismatch} — these should agree`
              : ghostCohort ? 'the Ghost Users card, member for member' : `${ds.length} day${ds.length === 1 ? '' : 's'} in range`}
          </div>
        </div>
        <div className="dd-kpi"><div className="k">Reached {lastScreenLabel}</div><div className="v serif">{reachedLastScreen}<small>{signups ? r1((reachedLastScreen / signups) * 100) : 0}%</small></div><div className="d">of everyone who started</div></div>
        <div className="dd-kpi r"><div className="k">Biggest single leak</div><div className="v serif">{worst ? worst.total : 0}</div><div className="d">{worst ? `never reached ${STEPS[worst.k]}` : '—'}</div></div>
        {ghostCohort ? (
          <div className="dd-kpi a"><div className="k">Left before the photo</div><div className="v serif">{beforeGate}<small>{signups ? r1((beforeGate / signups) * 100) : 0}%</small></div><div className="d">gave up before uploading a profile picture</div></div>
        ) : (
          <div className="dd-kpi"><div className="k">Completed profile</div><div className="v serif">{completedProfile}<small>{signups ? r1((completedProfile / signups) * 100) : 0}%</small></div><div className="d">100% + approved selfie + a photo</div></div>
        )}
      </div>

      <div className="dd-sec">
        <h3 className="dd-h">Daily drop-off by step</h3>
        <p className="dd-sub">
          {/* The rows are named for the step they never reached, which is the OPPOSITE of what
              the same name means on the funnel above (there it is the step they DID reach).
              One row is one gap, so it can only be named after one of its two ends — say which. */}
          A row is <b>everyone who never reached that step</b> — “{STEPS[1] || 'Gender'}” is the people who
          stopped on {STEPS[0] || 'the step before'}. All <b>{per.length} transitions</b> across <b>{ds.length} days</b>.
          Shade means <b>unusual for that row</b>, never <i>big</i> —
          every row is scaled to its own mean, so a dark cell in one row and a dark cell in another are not the same number of people.
          Compare rows with the <b>mean per day</b> in the label.{' '}
          {totalAnom
            ? <b style={{ color: 'var(--dd-accent)' }}>{totalAnom} day{totalAnom === 1 ? '' : 's'} beyond 2σ across the flow.</b>
            : 'No day is beyond 2σ.'}
        </p>

        <div className="dd-toolbar">
          <div className="dd-fg"><span className="dd-lab">View</span>
            <Pills items={[['cal', 'Calendar'], ['graph', 'Graph']]} value={view} onChange={setView} variant="acc" />
          </div>
          {view === 'graph' && (
            <div className="dd-fg"><span className="dd-lab">Chart</span>
              <Pills items={[['bars', 'Bars'], ['lolli', 'Lollipop'], ['dev', 'Deviation'], ['line', 'Line'], ['week', 'Weekday']]} value={chart} onChange={setChart} />
            </div>
          )}
          <div className="dd-fg"><span className="dd-lab">Shows</span>
            <Pills items={[['lost', 'Users lost'], ['rate', 'Drop % of step'], ['entry', '% of all signups']]} value={metric} onChange={setMetric} />
          </div>
        </div>

        <div className="dd-legend">
          <span className="chip"><span>quiet day</span><span className="ramp">{P.ramp.map((rr, i) => <i key={i} style={{ background: rr[0] }} />)}</span><span>bad day</span></span>
          <span className="chip"><span className="bx a" /> Beyond 2σ of this step’s own mean</span>
          {view === 'cal' && (
            <span className="chip"><span className="bx sel" /> <b>Click any cell</b>&nbsp;to see who was lost that day &amp; why</span>
          )}
        </div>

        {view === 'cal'
          ? <div ref={calRef} className={`dd-scroll${gridFits ? ' fits' : ''}`} onClick={onCellClick} role="presentation"><table className="dd-grid" dangerouslySetInnerHTML={{ __html: calHtml }} /></div>
          : <div className="dd-steps" dangerouslySetInnerHTML={{ __html: graphHtml }} />}
      </div>

      {sel && (
        <div className="dd-sec dd-drill" ref={drillRef}>
          <Drill
            sel={sel}
            STEPS={STEPS}
            data={drill}
            loading={drillLoading}
            error={drillError}
            genderKey={genderKey}
            platform={platform}
            cell={(per.find((p) => p.k === sel.k) || { rows: [] }).rows.find((r) => r.d === sel.d)}
            onClose={() => { setSel(null); setDrill(null); }}
          />
        </div>
      )}
    </div>
  );
};

/**
 * The drill-down panel: who was lost at this transition on this day, and — when the
 * transition is More photos → Bio — why, from the bios they tried to save.
 */
const Drill = ({ sel, STEPS, data, loading, error, cell, genderKey, platform, onClose }) => {
  // Which rows have their About submissions unfolded. A Set, not a single id: the whole
  // point of this cell is comparing what several refused members wrote, which you can't do
  // if opening one closes the last. `Expand all` is one tap away for the same reason.
  const [openAbout, setOpenAbout] = useState(() => new Set());
  const toggleAbout = (id) => setOpenAbout((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const dt = new Date(sel.d + 'T00:00:00');
  const nice = `${DOWFULL[dt.getDay()]}, ${dt.getDate()} ${MON[dt.getMonth()]}`;
  const users = data?.users || [];

  // More photos → Bio only: what each listed member typed into the bio, and how moderation
  // answered. Arrives with the cell, so the column is scannable before anything is tapped.
  const hasAbout = !!data?.hasAbout;
  const abt = data?.aboutSummary;
  const abtCats = abt ? ABOUT_CATS.filter((c) => abt[c.k] > 0) : [];
  // Only rows with something to read are worth expanding — "expand all" means these.
  const withAbout = hasAbout ? users.filter((u) => u.about?.length) : [];
  const allOpen = !!withAbout.length && withAbout.every((u) => openAbout.has(u.id));
  const toggleAll = () => setOpenAbout(allOpen ? new Set() : new Set(withAbout.map((u) => u.id)));

  // Export the table exactly as shown — same rows, same columns (the About columns only
  // when the transition has a bio story). The endpoint isn't paginated: it
  // returns the WHOLE cell (5000-row safety valve aside), so this is the full data set,
  // already narrowed by the page's period/gender/platform filters server-side.
  const exportCsv = () => {
    if (!users.length) return;
    const headers = [
      { key: 'idx', label: '#' },
      { key: 'email', label: 'User' },
      { key: 'stuckAt', label: 'Stuck at' },
      { key: 'signedUp', label: 'Signed up' },
      { key: 'platform', label: 'Platform' },
      // One row per user, so the attempts are flattened into a single cell rather than
      // multiplying the rows — the file stays joinable on the user.
      ...(hasAbout ? [
        { key: 'aboutTries', label: 'About attempts' },
        { key: 'aboutVerdict', label: 'About verdict' },
        { key: 'aboutTexts', label: 'What they wrote' },
        { key: 'aboutReasons', label: 'Why it was refused' },
      ] : []),
    ];
    const rows = users.map((u, i) => ({
      idx: i + 1,
      email: u.email || '',
      stuckAt: `step ${u.step} · ${STEPS[sel.k - 1]}${u.deleted ? ' · account deleted' : ''}`,
      signedUp: csvDT(u.signupAt),
      platform: u.platform === 'app' ? 'App' : 'Web',
      ...(hasAbout ? {
        aboutTries: (u.about || []).length,
        aboutVerdict: aboutVerdict(u.about),
        aboutTexts: (u.about || []).map((a) => a.text).join(' | '),
        aboutReasons: (u.about || []).filter((a) => !a.accepted && a.reason).map((a) => a.reason).join(' | '),
      } : {}),
    }));
    const filt = [
      platform && platform !== 'all' ? platform : '',
      genderKey && genderKey !== 'all' ? genderKey : '',
    ].filter(Boolean).join('-');
    downloadCsv({
      fileName: `onboarding-dropoff-users-${sel.d}-step${sel.k}${filt ? `-${filt}` : ''}.csv`,
      headers,
      rows,
    });
  };

  return (
    <>
      <div className="dd-drill-h">
        <div>
          <h3 className="dd-h">Who never reached {STEPS[sel.k]}</h3>
          <p className="dd-sub" style={{ margin: '7px 0 0' }}>
            <b>{nice}</b>
            {cell ? <> · the cell counts <b>{cell.lost} lost</b> of {cell.before} who reached {STEPS[sel.k - 1]} ({r1(cell.rate)}% of the step).</> : null}
            {/* The server caps the sample but still counts the whole cell, so say so plainly
                rather than letting the table's length read as the total. */}
            {data?.capped ? <> Listing the first <b>{data.shown}</b> of <b>{data.lost}</b>.</> : null}
          </p>
        </div>
        <div className="dd-drill-btns">
          <button type="button" className="dd-export" onClick={exportCsv} disabled={loading || !!error || !users.length}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 2v8m0 0L5 7m3 3l3-3M3 12h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Export
          </button>
          <button type="button" className="dd-close" onClick={onClose}>Close ✕</button>
        </div>
      </div>

      {loading && <div className="dd-dempty">Loading the people behind this cell…</div>}
      {!loading && error && <div className="dd-dempty">Couldn’t load the users for this cell. Try again, or reload the backend if the endpoint hasn’t shipped yet.</div>}
      {!loading && !error && !users.length && (
        <div className="dd-dempty">Nobody stopped before <b>{STEPS[sel.k]}</b> on <b>{nice}</b>.</div>
      )}

      {!loading && !error && !!users.length && (
        <>
          {/* More photos → Bio: split the cell by what the bio filter did, so
              "109 lost" resolves into how many were actually turned away. Every other step's
              drop is a plain product question, so the list itself is the whole answer. */}
          {hasAbout && !!abtCats.length && (
            <div className="dd-rchips">
              {abtCats.map((c) => (
                <span key={c.k} className={`dd-rchip ${c.cls}`}>
                  <span className="n">{abt[c.k]}</span> {c.label}
                </span>
              ))}
              {!!withAbout.length && (
                <button type="button" className="dd-xall" onClick={toggleAll}>
                  {allOpen ? 'Collapse all' : `Read all ${withAbout.length}`}
                </button>
              )}
            </div>
          )}

          <div className="dd-uscroll">
            <table className="dd-ut">
              <thead>
                <tr>
                  <th>#</th><th>User</th><th>Signed up</th><th>Platform</th>
                  {hasAbout && <th>About</th>}
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => {
                  const tries = u.about || [];
                  const verdict = aboutVerdict(tries);
                  const open = hasAbout && openAbout.has(u.id);
                  return (
                    <React.Fragment key={u.id || i}>
                    <tr className={open ? 'on' : ''}>
                      <td className="idx">{String(i + 1).padStart(2, '0')}</td>
                      <td className="em">
                        {u.email || '—'}
                        <span className="sub2">
                          stuck at step {u.step} · {STEPS[sel.k - 1]}
                          {u.deleted ? ' · account deleted' : ''}
                        </span>
                      </td>
                      <td>{tfmt(u.signupAt)}</td>
                      <td>{u.platform === 'app' ? 'App' : 'Web'}</td>
                      {hasAbout && (
                        <td className="abc">
                          {/* Nothing to open when they never typed — a dash reads faster than a
                              button that expands into "nothing here". */}
                          {!tries.length ? <span className="dd-abdash">never typed</span> : (
                            <button type="button" className={`dd-about${open ? ' on' : ''}`} onClick={() => toggleAbout(u.id)}>
                              <span className={`dd-tag ${verdict === 'blocked' ? 'b' : 'g'}`}>
                                {verdict === 'blocked' ? 'Refused' : 'Accepted'}
                              </span>
                              <span className="ct">{tries.length === 1 ? '1 try' : `${tries.length} tries`}</span>
                              <i aria-hidden="true">{open ? '▾' : '▸'}</i>
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                    {open && (
                      <tr className="dd-abrow">
                        <td colSpan={5}>
                          <div className="dd-abwrap">
                            {tries.map((a, n) => (
                              <div className={`dd-ab${a.accepted ? ' ok' : ''}`} key={`${u.id}-${n}`}>
                                <div className="dd-abh">
                                  <span className="dd-abn">Attempt {n + 1}</span>
                                  <span className="dd-abt">{tfmt(a.at)}</span>
                                  {a.accepted && <span className="dd-tag g">Accepted</span>}
                                </div>
                                <p className="dd-abx">{a.text || <em>(empty)</em>}</p>
                                {!a.accepted && a.reason && (
                                  <p className="dd-abr"><b>Refused:</b> {a.reason}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* What to actually do about each verdict present. */}
          {hasAbout && !!abtCats.length && (
            <div className="dd-acts">
              {abtCats.map((c) => (
                <div className="dd-actrow" key={c.k}>
                  <span className={`dd-tag ${c.cls}`}>{abt[c.k]} {c.k}</span>
                  <span>{c.act}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
};

const STYLES = `
.favor-dd{--ink:#25313f;--ink-2:#3f4854;--muted:#7c8590;--faint:#9aa2ac;--line:#ececec;--line-2:#e3e3e3;--wash:#f4f2ef;--red:#e15241;--red-d:#d43f2d;--green:#2f8f5b;--gold:#c08a2e;--dd-accent:#8f2318;
  --se:"ivypresto-headline",Georgia,serif;--sa:"indivisible","Neue Montreal","Helvetica Neue",Arial,sans-serif;
  --card-sh:0 1px 2px rgba(20,25,35,.04),0 6px 18px -10px rgba(20,25,35,.10);
  color:var(--ink-2);font-family:var(--sa);font-size:14px;line-height:1.5;-webkit-font-smoothing:antialiased;margin-top:20px}
.favor-dd *{box-sizing:border-box}
.favor-dd .serif{font-family:var(--se)}
.favor-dd .dd-empty{border:1px dashed var(--line-2);border-radius:14px;padding:40px;text-align:center;color:var(--muted);font-style:italic}
.favor-dd .dd-filters{display:flex;flex-wrap:wrap;gap:20px 26px;align-items:center;margin:8px 0 10px}
.favor-dd .dd-fg{display:flex;align-items:center;gap:12px}
.favor-dd .dd-lab{font-size:11px;font-weight:700;letter-spacing:.12em;color:var(--faint);text-transform:uppercase}
.favor-dd .dd-pills{display:inline-flex;background:var(--wash);border:1px solid var(--line);border-radius:11px;padding:4px}
.favor-dd .dd-pill{border:0;background:transparent;font-family:inherit;font-size:12.5px;font-weight:600;color:var(--ink-2);padding:7px 13px;border-radius:8px;cursor:pointer;white-space:nowrap;transition:.12s}
.favor-dd .dd-pill:hover{color:var(--ink)}
.favor-dd .dd-pill.on{background:#fff;color:var(--ink);box-shadow:0 1px 3px rgba(0,0,0,.12)}
.favor-dd .dd-pill.on.acc{background:var(--dd-accent);color:#fff;box-shadow:0 2px 8px -2px rgba(20,25,35,.28)}
.favor-dd .dd-pals{display:flex;gap:14px;flex-wrap:wrap}
.favor-dd .dd-pal{border:1px solid var(--line-2);background:#fff;border-radius:12px;padding:10px 12px 11px;cursor:pointer;display:flex;flex-direction:column;gap:7px;min-width:116px}
.favor-dd .dd-pal:hover{border-color:var(--muted)}
.favor-dd .dd-pal[aria-pressed=true]{border-color:var(--ink);box-shadow:0 0 0 1px var(--ink)}
.favor-dd .dd-pal .pn{font-size:12.5px;font-weight:700;color:var(--ink)}
.favor-dd .dd-pal .pr{display:flex;border-radius:4px;overflow:hidden}
.favor-dd .dd-pal .pr i{flex:1;height:12px;display:block}
.favor-dd .dd-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:13px;margin:24px 0 26px}
.favor-dd .dd-kpi{border:1px solid var(--line);border-radius:14px;padding:15px 17px;box-shadow:var(--card-sh);background:#fff}
.favor-dd .dd-kpi .k{font-size:10.5px;font-weight:700;letter-spacing:.05em;color:var(--faint);text-transform:uppercase;margin-bottom:8px}
.favor-dd .dd-kpi .v{font-weight:700;font-size:19px;line-height:1;color:var(--ink);letter-spacing:-.01em}
.favor-dd .dd-kpi .v small{font-family:var(--sa);font-size:11.5px;font-weight:700;margin-left:5px}
.favor-dd .dd-kpi .d{font-size:11.5px;color:var(--muted);margin-top:6px}
.favor-dd .dd-kpi.r{background:linear-gradient(180deg,#fdf2f0,#fff);border-color:#f7dad4}.favor-dd .dd-kpi.r .v{color:var(--red-d)}
.favor-dd .dd-kpi.a{background:linear-gradient(180deg,#fdf7e9,#fff);border-color:#f0e4c6}.favor-dd .dd-kpi.a .v{color:var(--gold)}
.favor-dd .dd-sec{border:1px solid var(--line);border-radius:16px;padding:20px 22px;box-shadow:var(--card-sh)}
.favor-dd .dd-h{font-family:var(--sa);font-weight:600;font-size:24px !important;line-height:1.3;color:var(--ink);margin:0}
.favor-dd .dd-sub{font-size:12px;color:var(--muted);margin:6px 0 18px;line-height:1.5}
.favor-dd .dd-sub b{color:var(--ink-2);font-weight:600}
.favor-dd .dd-toolbar{display:flex;flex-wrap:wrap;gap:16px 24px;align-items:center;margin-bottom:18px}
.favor-dd .dd-legend{display:flex;gap:18px;flex-wrap:wrap;align-items:center;margin-bottom:18px;padding:12px 15px;background:var(--wash);border-radius:12px;font-size:12px;color:var(--muted)}
.favor-dd .dd-legend .ramp{display:inline-flex;border-radius:4px;overflow:hidden;margin:0 4px}
.favor-dd .dd-legend .ramp i{width:24px;height:11px;display:block}
.favor-dd .dd-legend .chip{display:inline-flex;align-items:center;gap:8px}
.favor-dd .dd-legend .bx{width:13px;height:13px;border-radius:4px;border:1px solid var(--line)}
.favor-dd .dd-legend .bx.a{box-shadow:inset 0 0 0 2px var(--dd-accent)}
.favor-dd .dd-legend .bx.sel{box-shadow:inset 0 0 0 2px var(--ink)}
.favor-dd .dd-legend b{color:var(--ink);font-weight:600}
/* Fallback for a grid too wide for the card: it owns the horizontal scroll, and a box
   that scrolls on one axis scrolls on both — so the sticky thead pins to the box and
   the height cap is what gives it something to scroll against. */
.favor-dd .dd-scroll{max-height:calc(100vh - 9rem);overflow:auto;border:1px solid var(--line);border-radius:14px}
/* .fits (set by JS when no horizontal scrollbar is needed — the common case): no
   overflow at all, so the page is the scrollport. The day header then sticks under
   the app's 4.5rem .navbar-header, below its z-index:2 so it slides beneath the bar.
   The left Transition column un-sticks in tbody — nothing scrolls sideways here, and
   leaving it sticky would let those cells paint over the sticky header row. */
.favor-dd .dd-scroll.fits{max-height:none;overflow:visible}
.favor-dd .dd-scroll.fits table.dd-grid thead th{top:4.5rem;z-index:1}
.favor-dd .dd-scroll.fits table.dd-grid tbody th.rl{position:static}
/* Extra head padding so the pinned header doesn't sit flush under the navbar — it's
   padding rather than a bigger top offset, which would open a gap the rows show
   through as they scroll past. */
.favor-dd .dd-scroll.fits table.dd-grid thead th.dh{padding-top:16px}
.favor-dd .dd-scroll.fits table.dd-grid thead th.rl{padding-top:18px}
.favor-dd .dd-scroll.fits table.dd-grid thead th:first-child{border-top-left-radius:13px}
.favor-dd .dd-scroll.fits table.dd-grid thead th:last-child{border-top-right-radius:13px}
.favor-dd table.dd-grid{border-collapse:separate;border-spacing:0;width:100%;background:#fff}
.favor-dd table.dd-grid th,.favor-dd table.dd-grid td{padding:0}
.favor-dd table.dd-grid thead th{position:sticky;top:0;background:#fff;z-index:3;border-bottom:1px solid var(--line-2)}
.favor-dd table.dd-grid th.rl{position:sticky;left:0;z-index:2;background:#fff;text-align:left;min-width:270px;width:270px;padding:9px 16px 10px 14px;border-right:1px solid var(--line);border-bottom:1px solid #f4f4f4}
.favor-dd table.dd-grid thead th.rl{z-index:4;border-bottom:1px solid var(--line-2)}
.favor-dd table.dd-grid .lab{font-size:11px;font-weight:700;letter-spacing:.12em;color:var(--faint);text-transform:uppercase}
.favor-dd table.dd-grid .rn{font-family:var(--se);font-weight:700;font-size:13px;color:var(--faint);margin-right:7px}
.favor-dd table.dd-grid .rt{font-size:12.5px;font-weight:600;color:var(--ink);display:inline}
.favor-dd table.dd-grid .rt i{font-style:normal;color:var(--muted);margin:0 3px}
.favor-dd table.dd-grid .rm{font-size:11px;color:var(--muted);margin-top:3px;font-variant-numeric:tabular-nums}
.favor-dd table.dd-grid .rm b{color:var(--ink);font-weight:600}
.favor-dd table.dd-grid .rm .flag{color:var(--dd-accent);font-weight:600}
.favor-dd table.dd-grid th.dh{min-width:40px;padding:7px 2px 8px;text-align:center;vertical-align:bottom}
.favor-dd table.dd-grid th.dh .mo{display:block;font-size:9px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--dd-accent);height:11px}
.favor-dd table.dd-grid th.dh .dw{display:block;font-size:9px;font-weight:700;color:var(--faint);margin-top:1px}
.favor-dd table.dd-grid th.dh .dn{display:block;font-size:12px;font-weight:600;color:var(--ink-2);margin-top:1px}
.favor-dd table.dd-grid th.dh.we .dw,.favor-dd table.dd-grid th.dh.we .dn{color:var(--faint)}
.favor-dd table.dd-grid th.dh.ms,.favor-dd table.dd-grid td.ms{border-left:1px solid var(--line-2)}
.favor-dd table.dd-grid td.c{height:38px;text-align:center;vertical-align:middle;line-height:38px;font-size:11.5px;font-weight:700;font-variant-numeric:tabular-nums;position:relative;border-right:1px solid rgba(255,255,255,.75);border-bottom:1px solid rgba(255,255,255,.75);cursor:pointer}
.favor-dd table.dd-grid td.c:hover{box-shadow:inset 0 0 0 2px var(--ink);z-index:2}
.favor-dd table.dd-grid td.c.sel{box-shadow:inset 0 0 0 2.5px var(--ink);z-index:2}
.favor-dd table.dd-grid td.c.anom:hover,.favor-dd table.dd-grid td.c.anom.sel{box-shadow:inset 0 0 0 2.5px var(--ink);z-index:2}
.favor-dd table.dd-grid td.c.blank{background:repeating-linear-gradient(-45deg,#fafafa,#fafafa 3px,#f2f2f0 3px,#f2f2f0 6px);color:transparent;cursor:default}
.favor-dd table.dd-grid td.c.blank:hover{box-shadow:none}
.favor-dd table.dd-grid td.c.anom{box-shadow:inset 0 0 0 2px var(--ringc);z-index:1}
.favor-dd table.dd-grid td.c .dot{position:absolute;top:3px;right:3px;width:4px;height:4px;border-radius:50%;background:var(--ringc)}
.favor-dd table.dd-grid tbody tr:hover th.rl{background:var(--wash)}
.favor-dd table.dd-grid tr.hot th.rl{background:var(--wash)}
.favor-dd .dd-steps{display:grid;grid-template-columns:repeat(auto-fit,minmax(640px,1fr));gap:18px}
.favor-dd .dd-steps .sc{border:1px solid var(--line);border-radius:14px;padding:15px 17px 16px;box-shadow:var(--card-sh);background:#fff}
.favor-dd .dd-steps .sc-h{display:flex;align-items:baseline;gap:9px;margin-bottom:3px}
.favor-dd .dd-steps .sc-n{font-family:var(--se);font-weight:700;font-size:15px;color:var(--faint)}
.favor-dd .dd-steps .sc-t{font-size:13.5px;font-weight:600;color:var(--ink)}
.favor-dd .dd-steps .sc-t i{font-style:normal;color:var(--muted);margin:0 3px}
.favor-dd .dd-steps .sc-m{font-size:11.5px;color:var(--muted);margin-bottom:11px;font-variant-numeric:tabular-nums}
.favor-dd .dd-steps .sc-m b{color:var(--ink);font-weight:600}
.favor-dd .dd-steps .sc-m .flag{color:var(--dd-accent);font-weight:600}
.favor-dd .dd-steps .gwrap{overflow-x:auto}
.favor-dd .dd-steps .gwrap svg{display:block;width:100%;min-width:620px;height:210px}
.favor-dd .dd-steps .axis{font-size:9.5px;fill:var(--faint);font-weight:600}
.favor-dd .dd-steps .vlab{font-size:9.5px;font-weight:700;fill:var(--ink)}
.favor-dd .dd-steps .vlab.hi{fill:var(--dd-accent)}
.favor-dd .dd-steps .gnote{font-size:11px;color:var(--faint);margin-top:6px}
/* ── drill-down: who dropped here & why ── */
.favor-dd .dd-drill{margin-top:20px}
.favor-dd .dd-drill-h{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap;margin-bottom:4px}
.favor-dd .dd-drill-btns{display:flex;align-items:center;gap:10px;flex-shrink:0}
.favor-dd .dd-close{border:1px solid var(--line-2);background:#fff;font:inherit;font-size:12.5px;font-weight:600;color:var(--ink-2);border-radius:10px;padding:8px 14px;cursor:pointer;flex-shrink:0}
.favor-dd .dd-close:hover{border-color:var(--ink)}
.favor-dd .dd-export{display:inline-flex;align-items:center;gap:7px;border:1px solid var(--dd-accent);background:var(--dd-accent);font:inherit;font-size:12.5px;font-weight:600;color:#fff;border-radius:10px;padding:8px 14px;cursor:pointer;flex-shrink:0;transition:.12s}
.favor-dd .dd-export:hover:not(:disabled){filter:brightness(1.15)}
.favor-dd .dd-export:disabled{opacity:.45;cursor:default}
.favor-dd .dd-dempty{border:1px dashed var(--line-2);border-radius:14px;padding:26px 24px;text-align:center;color:var(--muted);font-size:13px;margin-top:16px;line-height:1.6}
.favor-dd .dd-dempty b{color:var(--ink)}
.favor-dd .dd-rchips{display:flex;gap:10px;flex-wrap:wrap;margin:18px 0 4px}
.favor-dd .dd-rchip{display:inline-flex;align-items:center;gap:9px;font-size:12.5px;font-weight:600;border-radius:11px;padding:9px 14px;border:1px solid var(--line)}
.favor-dd .dd-rchip .n{font-family:var(--se);font-weight:700;font-size:16px}
.favor-dd .dd-rchip.b{background:#fdf0ee;border-color:#f3cfc8;color:#8f2318}
.favor-dd .dd-rchip.o{background:#eef3fb;border-color:#d3e0f4;color:#1e3a78}
.favor-dd .dd-rchip.n{background:#f5f5f4;border-color:#e3e3e3;color:#5c646d}
/* The stuck-user list can run long, and a box that scrolls on one axis scrolls on
   both — so this stays a scroll box (rather than riding the page scroll) and the
   header pins to it at top:0. Capping the height is what makes the pin useful:
   without it the box never scrolls and the header would just leave with the page. */
.favor-dd .dd-uscroll{max-height:calc(100vh - 12rem);overflow:auto;border:1px solid var(--line);border-radius:14px;margin-top:16px}
.favor-dd table.dd-ut{border-collapse:collapse;width:100%;background:#fff}
.favor-dd table.dd-ut thead th{position:sticky;top:0;z-index:1;background:var(--wash);border-bottom:1px solid var(--line-2);box-shadow:inset 0 -1px 0 var(--line-2);padding:10px 14px;text-align:left;font-size:10.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--faint);white-space:nowrap}
.favor-dd table.dd-ut td{padding:11px 14px;border-bottom:1px solid #f4f4f4;font-size:12.5px;color:var(--ink-2);vertical-align:top;white-space:nowrap}
.favor-dd table.dd-ut tr:last-child td{border-bottom:none}
.favor-dd table.dd-ut tr:hover td{background:#fcfbf9}
.favor-dd table.dd-ut .em{font-weight:600;color:var(--ink)}
.favor-dd table.dd-ut .idx{font-family:var(--se);font-weight:700;color:var(--faint)}
.favor-dd table.dd-ut .sub2{display:block;font-size:10.5px;color:var(--muted);margin-top:2px;font-weight:500}
.favor-dd table.dd-ut .ok{color:var(--green);font-weight:700}
.favor-dd table.dd-ut .no{color:var(--red-d);font-weight:700}
.favor-dd .dd-tag{display:inline-block;font-size:10.5px;font-weight:700;border-radius:20px;padding:3px 10px}
.favor-dd .dd-tag.b{background:#fdf0ee;color:#8f2318}
.favor-dd .dd-tag.o{background:#eef3fb;color:#1e3a78}
.favor-dd .dd-tag.n{background:#f5f5f4;color:#5c646d}
.favor-dd .dd-tag.g{background:#e9f4ed;color:#14472c}
/* ── About column (More photos → Bio) ───────────────────────────────────
   The cell carries its verdict WITHOUT being opened — the column is meant to be
   scanned down, so "Refused ×3" has to be legible at a glance and the disclosure
   arrow is the quiet part. The texts then unfold in a row beneath rather than a
   modal, which is what lets several refused bios be read in one pass. */
.favor-dd .dd-xall{margin-left:auto;align-self:center;border:1px solid var(--line-2);background:#fff;border-radius:11px;padding:9px 14px;font:inherit;font-size:12px;font-weight:600;color:var(--ink-2);cursor:pointer}
.favor-dd .dd-xall:hover{border-color:var(--faint);background:var(--wash)}
.favor-dd table.dd-ut td.abc{white-space:nowrap}
.favor-dd .dd-abdash{font-size:12px;color:var(--faint)}
.favor-dd .dd-about{border:0;background:none;padding:0;font:inherit;cursor:pointer;display:inline-flex;align-items:center;gap:7px;text-align:left}
.favor-dd .dd-about .ct{font-size:11.5px;font-weight:600;color:var(--muted)}
.favor-dd .dd-about i{font-style:normal;font-size:9px;color:var(--faint);transition:transform .12s ease}
.favor-dd .dd-about:hover .ct{color:var(--ink)}
.favor-dd .dd-about:hover .dd-tag{filter:brightness(.97)}
.favor-dd .dd-about.on .ct{color:var(--ink)}
/* The open row and its detail read as one block, so tint the parent too. */
.favor-dd table.dd-ut tr.on td{background:#fbfaf8}
.favor-dd table.dd-ut tr.on td{border-bottom-color:transparent}
.favor-dd table.dd-ut tr.dd-abrow td{background:#fbfaf8;white-space:normal;padding:0 14px 16px 46px}
.favor-dd .dd-abwrap{display:flex;flex-direction:column;gap:10px;max-width:820px}
.favor-dd .dd-ab{border:1px solid #f0e6e4;border-left:3px solid var(--dd-accent);border-radius:0 10px 10px 0;background:#fff;padding:11px 14px}
.favor-dd .dd-ab.ok{border-color:#dcebe2;border-left-color:var(--green)}
.favor-dd .dd-abh{display:flex;align-items:center;gap:10px;margin-bottom:6px}
.favor-dd .dd-abn{font-family:var(--se);font-weight:700;font-size:12px;color:var(--ink)}
.favor-dd .dd-abt{font-size:10.5px;color:var(--muted);font-weight:500}
/* The bio itself is the thing being judged — set it apart from the admin chrome. */
.favor-dd .dd-abx{margin:0;font-size:13px;color:var(--ink);line-height:1.6;white-space:pre-wrap;word-break:break-word}
.favor-dd .dd-abx em{color:var(--faint)}
.favor-dd .dd-abr{margin:8px 0 0;padding-top:8px;border-top:1px dashed #f0e6e4;font-size:11.5px;color:var(--red-d);line-height:1.5}
.favor-dd .dd-abr b{font-weight:700}
.favor-dd .dd-acts{margin:18px 0 0;display:flex;flex-direction:column;gap:10px}
.favor-dd .dd-actrow{display:flex;gap:12px;align-items:flex-start;font-size:12.5px;color:var(--ink-2);line-height:1.5}
.favor-dd .dd-actrow .dd-tag{flex-shrink:0;margin-top:1px}
@media(max-width:820px){.favor-dd .dd-kpis{grid-template-columns:repeat(2,1fr)}}
@media(max-width:640px){.favor-dd .dd-steps{grid-template-columns:1fr}}
`;

export default DailyDropoff;
